import {
  Breadcrumbs,
  Box,
  Button,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/joy';
import { CreateEditSellForm } from '@/components/Pages/Sells/Create/CreateEditSellForm';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { pageTitle } from '@/utils/pageTitle';
import { PaymentModal } from '@/components/common/PaymentModal';
import { PrintInvoice } from '@/components/Pages/Sells/PrintInvoice';
import { OrderResponseDTO } from '@/types';
import { SellEditProvider, useSellEdit } from '@/contexts/SellEditContext';
import { useLedgers } from '@/hooks/useLedgers';
import { useMemo } from 'react';
import { useOrder, useCancelOrder } from '@/hooks/useOrders';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/sells/$id')({
  component: SellDetail,
  head: ({ params }) => pageTitle(`Ventas / ${params.id}`),
});

function SellDetail() {
  const { id } = Route.useParams();
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('SELLS_READ');
  const canWrite = hasClaim('SELLS_WRITE');

  const { data: order, isLoading } = useOrder(id);

  const { data: payments } = useLedgers({
    active: true,
    referenceId: order?.id,
    clientId: order?.clientId,
    enabled: !!order?.id,
  });

  const hasPayments = useMemo(() => {
    return !!(payments && payments.length > 0);
  }, [payments]);

  const totalPaid = useMemo(() => {
    return payments?.reduce((acc, payment) => acc + payment.amount, 0);
  }, [payments]);

  const { mutate: cancelOrder, isPending: isCancellingOrder } =
    useCancelOrder();

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/dashboard' />;
  }

  if (!order) {
    return <div>venta no encontrada</div>;
  }

  // Inner component to use context
  const SellDetailWithEditContext = ({
    order,
    hasPayments,
  }: {
    order: OrderResponseDTO;
    hasPayments: boolean;
  }) => {
    const { isEdit, setIsEdit } = useSellEdit();
    return (
      <>
        <Stack
          direction='row'
          justifyContent='space-between'
          mb={2}
          flexWrap='wrap'
        >
          <Breadcrumbs size='sm'>
            <Link to='/sells'>Ventas</Link>
            <Typography>{id}</Typography>
          </Breadcrumbs>
          {canWrite && (
            <Stack direction='row' gap={2} flexWrap='wrap'>
              <Button
                variant='outlined'
                color='neutral'
                onClick={() => setIsEdit(!isEdit)}
                disabled={order.status !== 'Confirmed' || hasPayments}
              >
                {isEdit ? 'Cancelar' : 'Editar'}
              </Button>
              {order.status !== 'Paid' && order.status !== 'Cancelled' && (
                <Button
                  variant='outlined'
                  color='danger'
                  onClick={() => cancelOrder(id)}
                  loading={isCancellingOrder}
                  disabled={isEdit || hasPayments}
                >
                  Cancelar
                </Button>
              )}
              {order.status === 'Confirmed' && (
                <PaymentModal
                  clientId={order.clientId}
                  isEdit={isEdit}
                  orderId={order.id}
                  total={order.total}
                  subtotal={order.subtotal}
                  discount={order.discount}
                  totalPaid={totalPaid ?? 0}
                  paymentMethodId={order.paymentMethodId}
                  paymentMethodName={order.paymentMethodName}
                  paymentAdjustment={order.paymentAdjustment}
                />
              )}

              <PrintInvoice order={order} isEdit={isEdit} />
            </Stack>
          )}
        </Stack>

        <CreateEditSellForm order={order} />
      </>
    );
  };

  return (
    <SellEditProvider>
      <SellDetailWithEditContext order={order} hasPayments={hasPayments} />
    </SellEditProvider>
  );
}
